import { useLayoutEffect, useRef } from "react";
import { useDialogHost } from "../DialogHostMutationContext";
import { DIALOG_EXIT_MS } from "./dialogConstants";

/**
 * Owns this dialog's spot in the open-stack registry that drives z-index.
 *
 * On open: register synchronously. On close (open=false), or on full
 * unmount: schedule the unregister DIALOG_EXIT_MS later so the
 * stackPosition stays correct while the exit animation runs.
 *
 * setTimeout outlives the component — if the parent unmounts the dialog
 * mid-open, the timer still fires and calls unregister against the host.
 * unregister is idempotent on the host side, so a fire-after-host-unmount
 * lands as a no-op state update on an unmounted component (silently
 * dropped by React).
 */
export function useDialogStackRegistration(
  stackId: string,
  open: boolean,
): void {
  const { register, unregister } = useDialogHost();
  const unregisterTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  useLayoutEffect(() => {
    if (!open) return;
    // Cancel any pending unregister from a previous close-then-reopen.
    if (unregisterTimerRef.current) {
      clearTimeout(unregisterTimerRef.current);
      unregisterTimerRef.current = undefined;
    }
    register(stackId);
    return () => {
      unregisterTimerRef.current = setTimeout(() => {
        // Same guard as DialogModal_'s ghost-cleanup timer: vitest's jsdom
        // teardown strips `window` while pending timers can still fire,
        // and React's setState path would crash. No-op if we're past the
        // environment's lifetime.
        if (typeof window === "undefined") return;
        unregister(stackId);
        unregisterTimerRef.current = undefined;
      }, DIALOG_EXIT_MS);
    };
  }, [open, stackId, register, unregister]);
}
